import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Truck, 
  Package, 
  Users, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Shield, 
  ChevronRight,
  CheckCircle,
  ArrowRight,
  Menu,
  X
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    {
      icon: Truck,
      title: "Full Truck Load (FTL)",
      description: "Complete truck capacity for your large shipments with dedicated vehicles across India."
    },
    {
      icon: Package,
      title: "Part Truck Load (PTL)",
      description: "Cost-effective solution for smaller shipments sharing truck space with other consignments."
    },
    {
      icon: MapPin,
      title: "Pan-India Coverage",
      description: "Extensive network covering all major cities and industrial hubs across the nation."
    },
    {
      icon: Clock,
      title: "Express Delivery",
      description: "Time-critical deliveries with guaranteed transit times for urgent shipments."
    },
    {
      icon: Shield,
      title: "Insured Transit",
      description: "Complete insurance coverage for all goods ensuring peace of mind during transit."
    },
    {
      icon: Users,
      title: "Dedicated Fleet",
      description: "Modern fleet of vehicles with experienced drivers for reliable transportation."
    }
  ];

  const stats = [
    { value: "15+", label: "Years Experience" },
    { value: "500+", label: "Vehicles" },
    { value: "50K+", label: "Deliveries" },
    { value: "100+", label: "Cities Covered" }
  ];

  const features = [
    "Real-time shipment tracking",
    "24/7 customer support",
    "Competitive pricing",
    "On-time delivery guarantee",
    "GPS-enabled fleet",
    "Digital documentation"
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top Contact Bar */}
      <div className="bg-primary/10 border-b border-border py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-xs">
          <div className="flex items-center gap-6">
            <a href="tel:+919395170701" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="w-3 h-3" />
              +91 93951 70701
            </a>
            <a href="mailto:info.arrdhnageshwarlog@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Mail className="w-3 h-3" />
              info.arrdhnageshwarlog@gmail.com
            </a>
          </div>
          <p className="text-muted-foreground">Transport & Logistics Solutions</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/95 backdrop-blur-md shadow-lg md:top-0" : "bg-transparent md:top-[36px]"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_ardhana-log/artifacts/ucgxtluv_WhatsApp%20Image%202026-01-17%20at%2023.49.17.jpeg" 
                alt="Ardhanageshwar Logistics"
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg"
              />
              <div>
                <h1 className="font-heading text-lg md:text-xl font-bold text-primary">ARDHANAGESHWAR</h1>
                <p className="text-xs text-muted-foreground hidden sm:block">Logistics Pvt. Ltd.</p>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-sm hover:text-primary transition-colors">Services</a>
              <a href="#about" className="text-sm hover:text-primary transition-colors">About Us</a>
              <a href="#contact" className="text-sm hover:text-primary transition-colors">Contact</a>
              <Button 
                onClick={() => navigate("/login")}
                className="bg-primary hover:bg-primary/90"
                data-testid="nav-login-btn"
              >
                Login to TMS
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-background/95 backdrop-blur-md border-t border-border">
            <div className="px-4 py-4 space-y-4">
              <a href="#services" className="block text-sm hover:text-primary transition-colors" onClick={() => setIsMenuOpen(false)}>Services</a>
              <a href="#about" className="block text-sm hover:text-primary transition-colors" onClick={() => setIsMenuOpen(false)}>About Us</a>
              <a href="#contact" className="block text-sm hover:text-primary transition-colors" onClick={() => setIsMenuOpen(false)}>Contact</a>
              <Button 
                onClick={() => navigate("/login")}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Login to TMS
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
              <Truck className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Trusted Logistics Partner</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6">
              Delivering Excellence
              <span className="text-primary"> Across India</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
              Ardhanageshwar Logistics Pvt. Ltd. provides reliable, efficient, and technology-driven 
              transport solutions. Your cargo, our commitment.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="border-primary/50 hover:bg-primary/10 text-lg px-8"
              >
                Contact Us
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-8 border-t border-border/50">
              {stats.map((stat, index) => (
                <div key={index} className="text-center md:text-left">
                  <div className="text-3xl md:text-4xl font-bold text-primary">{stat.value}</div>
                  <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">Our Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive logistics solutions tailored to meet your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-card p-6 rounded-xl border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-muted-foreground">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
                Why Choose <span className="text-primary">Ardhanageshwar</span>?
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                With over 15 years of experience in the logistics industry, we have built a reputation 
                for reliability, efficiency, and customer satisfaction. Our modern fleet and dedicated 
                team ensure your cargo reaches its destination safely and on time.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                className="mt-8 bg-primary hover:bg-primary/90"
                onClick={() => navigate("/login")}
              >
                Start Shipping Today
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                  alt="Logistics Fleet"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">15+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Ready to Streamline Your Logistics?
          </h2>
          <p className="text-muted-foreground mb-8 text-lg">
            Access our Transport Management System (TMS) to track shipments, manage orders, 
            and optimize your supply chain operations.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate("/login")}
            className="bg-primary hover:bg-primary/90 text-lg px-10"
            data-testid="cta-login-btn"
          >
            Login to TMS Portal
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">Get In Touch</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Have questions? We're here to help. Reach out to us anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-xl border border-border text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Our Location</h3>
              <p className="text-muted-foreground text-sm">
                Guwahati, Assam<br />India
              </p>
            </div>

            <div className="bg-card p-6 rounded-xl border border-border text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Phone</h3>
              <p className="text-muted-foreground text-sm">
                <a href="tel:+919395170701" className="hover:text-primary transition-colors">+91 93951 70701</a><br />
                (Mon-Sat, 9AM-6PM)
              </p>
            </div>

            <div className="bg-card p-6 rounded-xl border border-border text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Email</h3>
              <p className="text-muted-foreground text-sm">
                <a href="mailto:info.arrdhnageshwarlog@gmail.com" className="hover:text-primary transition-colors">info.arrdhnageshwarlog@gmail.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ardhana-log/artifacts/ucgxtluv_WhatsApp%20Image%202026-01-17%20at%2023.49.17.jpeg" 
                  alt="Ardhanageshwar Logistics"
                  className="w-10 h-10 rounded-lg"
                />
                <div>
                  <h3 className="font-heading font-bold text-primary">ARDHANAGESHWAR</h3>
                  <p className="text-xs text-muted-foreground">Logistics Pvt. Ltd.</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                Your trusted partner for reliable and efficient logistics solutions across India.
              </p>
              <p className="text-muted-foreground text-xs">
                GST: 18ABECA5341A2Z4 | PAN: ABECA5341A
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#services" className="hover:text-primary transition-colors">Services</a></li>
                <li><a href="#about" className="hover:text-primary transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-primary transition-colors">Contact</a></li>
                <li><button onClick={() => navigate("/login")} className="hover:text-primary transition-colors">TMS Portal</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Full Truck Load</li>
                <li>Part Truck Load</li>
                <li>Express Delivery</li>
                <li>Warehousing</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Ardhanageshwar Logistics Pvt. Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
